import { AfterViewInit, Component, ElementRef, Input, NgZone, OnDestroy, ViewChild } from '@angular/core';
import { AttachmentKind, AttachmentPreviewService, StatementAttachment } from '../../../services/attachment-preview.service';

@Component({
  selector: 'app-attachment-thumbnail',
  template: `
    <div class="d-flex align-items-center justify-content-center border rounded bg-white overflow-hidden"
      [class.bg-light]="!rendered" [style.width.px]="width" [style.height.px]="height">
      @if (!rendered) {
        @if (loading) {
          <mat-spinner diameter="18" strokeWidth="2"></mat-spinner>
        } @else {
          <mat-icon color="primary">{{ preview.icon(attachment) }}</mat-icon>
        }
      }
      <canvas #canvas [hidden]="!rendered" class="mw-100 mh-100" (contextmenu)="$event.preventDefault()"></canvas>
    </div>
  `,
  styles: [':host { display: block; }'],
  standalone: false,
})
export class AttachmentThumbnailComponent implements AfterViewInit, OnDestroy {
  @Input({ required: true }) attachment!: StatementAttachment;
  @Input({ required: true }) surveyId!: number;
  @Input({ required: true }) questionId!: number;
  @Input() width = 44;
  @Input() height = 56;
  @ViewChild('canvas') canvasRef?: ElementRef<HTMLCanvasElement>;

  loading = false;
  rendered = false;
  private destroyed = false;
  private observer: IntersectionObserver | null = null;

  constructor(
    public preview: AttachmentPreviewService,
    private host: ElementRef<HTMLElement>,
    private zone: NgZone,
  ) {}

  get kind(): AttachmentKind {
    return this.preview.kind(this.attachment);
  }

  ngAfterViewInit(): void {
    if (this.kind !== 'pdf' && this.kind !== 'image') {
      return;
    }
    if (typeof IntersectionObserver === 'undefined') {
      setTimeout(() => this.render());
      return;
    }
    this.observer = new IntersectionObserver((entries) => {
      if (entries.some((entry) => entry.isIntersecting)) {
        this.observer?.disconnect();
        this.observer = null;
        this.zone.run(() => this.render());
      }
    }, { rootMargin: '200px' });
    this.observer.observe(this.host.nativeElement);
  }

  ngOnDestroy(): void {
    this.destroyed = true;
    this.observer?.disconnect();
  }

  private async render(): Promise<void> {
    const canvas = this.canvasRef?.nativeElement;
    if (!canvas || !this.surveyId || !this.questionId) {
      return;
    }
    this.loading = true;
    try {
      if (this.kind === 'pdf') {
        const pdf = await this.preview.pdf(this.surveyId, this.questionId, this.attachment);
        if (this.destroyed) {
          return;
        }
        await this.preview.renderPdfThumbnail(pdf, this.preview.pageOf(this.attachment), canvas, this.width * 2);
      } else {
        const url = await this.preview.objectUrl(this.surveyId, this.questionId, this.attachment);
        const image = await this.preview.loadImage(url);
        if (this.destroyed) {
          return;
        }
        const scale = Math.min((this.width * 2) / image.naturalWidth, (this.height * 2) / image.naturalHeight, 1);
        await this.preview.drawImage(url, canvas, scale);
      }
      canvas.style.width = '';
      canvas.style.height = '';
      this.rendered = true;
    } catch {
      this.rendered = false;
    } finally {
      this.loading = false;
    }
  }
}
